import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '@nucleus-commerce/admin-kit/layout';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { LegalLinks } from '@/shared/legal/legal-links';

export interface AuthPageProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Links under the card, e.g. back to sign in. */
  footer?: ReactNode;
  /** A wider card for long forms such as the brand application. */
  wide?: boolean;
}

/** Frame for the public account pages (invitation, password reset, brand application): brand, one card, footer links. */
export function AuthPage({ title, description, children, footer, wide = false }: AuthPageProps) {
  const { t } = useTranslation('auth');
  return (
    <div className="bg-muted/40 flex min-h-svh flex-col items-center justify-center gap-6 px-4 py-10">
      <Link to="/" className="flex items-center gap-2.5">
        <BrandMark size={26} />
        <span className="text-lg font-semibold tracking-tight">{t('landing.brand')}</span>
      </Link>
      <main
        className={cn(
          'bg-card text-card-foreground w-full rounded-2xl border p-7 shadow-[0_1px_2px_rgba(24,24,27,0.04),0_12px_32px_rgba(24,24,27,0.06)] sm:p-9',
          wide ? 'max-w-[600px]' : 'max-w-[420px]',
        )}
      >
        <div className="mb-5 flex flex-col gap-1.5">
          <h1 className="text-[22px] font-semibold tracking-tight">{title}</h1>
          {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
        </div>
        {children}
      </main>
      {footer ? (
        <div className="text-muted-foreground [&_a]:text-foreground flex flex-wrap justify-center gap-4 text-sm [&_a]:underline-offset-4 [&_a:hover]:underline">
          {footer}
        </div>
      ) : null}
      <LegalLinks className="text-muted-foreground [&_a:hover]:text-foreground text-xs [&_a]:underline-offset-4 [&_a:hover]:underline" />
    </div>
  );
}
